import { useCallback, useEffect, useMemo, useState } from 'react'
import { CreateToolModal } from '@/components/CreateToolModal'
import { cn } from '@/utils'

export const OPEN_TOOL_PLACEHOLDER_MODAL_EVENT =
  'slash-tools-open-placeholder-modal'

interface AddToolFormProps {
  query: string
  updateBomOnSelect: boolean
  isSelected?: boolean
  onToolCreated?: (tool: { id: string; name: string }) => Promise<void> | void
}

export const AddToolForm = ({
  query,
  updateBomOnSelect,
  isSelected,
  onToolCreated,
}: AddToolFormProps) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createModalInitialToolName, setCreateModalInitialToolName] =
    useState('')
  const normalizedQuery = useMemo(() => query.trim(), [query])

  const openPlaceholderModal = useCallback(
    (nextToolName?: string) => {
      setCreateModalInitialToolName(nextToolName?.trim() || normalizedQuery)
      setIsCreateModalOpen(true)
    },
    [normalizedQuery],
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleOpenModal = (event: Event) => {
      const customEvent = event as CustomEvent<{ query?: string }>
      openPlaceholderModal(customEvent.detail?.query)
    }

    window.addEventListener(
      OPEN_TOOL_PLACEHOLDER_MODAL_EVENT,
      handleOpenModal as EventListener,
    )

    return () => {
      window.removeEventListener(
        OPEN_TOOL_PLACEHOLDER_MODAL_EVENT,
        handleOpenModal as EventListener,
      )
    }
  }, [openPlaceholderModal])

  const content = useMemo(
    () =>
      updateBomOnSelect ? (
        <span
          className={cn('text-left px-2 py-1 rounded-md hover:bg-slate-300', {
            'bg-slate-200 ': isSelected,
          })}
        >
          Add&nbsp;
          <span
            style={{
              padding: '2px 6px',
              borderRadius: '5px',
              backgroundColor: '#E5D5FF',
              color: 'rgb(30, 30, 30)',
            }}
          >
            {query}
          </span>
          &nbsp;to Tools BOM?
        </span>
      ) : (
        <span
          style={{
            padding: '2px 6px',
            borderRadius: '5px',
            backgroundColor: '#E5D5FF',
            color: 'rgb(30, 30, 30)',
          }}
        >
          {query}
        </span>
      ),
    [updateBomOnSelect, query, isSelected],
  )

  return (
    <>
      {!isCreateModalOpen && (
        <button
          type="button"
          className="w-full text-left"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation()
            openPlaceholderModal()
          }}
        >
          <div className="px-2 py-2 break-all">{content}</div>
        </button>
      )}

      <CreateToolModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        initialToolName={createModalInitialToolName}
        ariaLabel="Create tool dialog"
        onToolCreated={onToolCreated}
      />
    </>
  )
}
